// Copy for the app's confirmation dialogs, as data.
//
// Kept out of the components because these sentences are the whole product
// decision. Each one has to answer, in the order a worried person asks:
// what is about to change, is anything lost, and can I undo it.
//
// Two words this file deliberately does not use:
//
//   "תצוגה" for the budget granularity. It is a stored restaurant setting that
//   decides which series future budgets are written into — calling it a view
//   invites people to flip it casually and then wonder where their budgets went.
//
//   "ינותקו" for what happens to a deleted classification's products. They are being
//   re-filed, not severed, and "יעברו ל'לא מסווג'" also tells the user where to
//   find them afterwards.

// `to` and `at` differ by one letter and it matters: you move TO monthly
// budgets (ל) but you stay IN them (ב).
const NOUN = {
  month: {
    budgets: 'החודשיים', one: 'חודשי', calc: 'החודשי', unit: 'חודש', plural: 'חודשים',
    to: 'ליעדים חודשיים', at: 'ביעדים חודשיים',
  },
  week: {
    budgets: 'השבועיים', one: 'שבועי', calc: 'השבועי', unit: 'שבוע', plural: 'שבועות',
    to: 'ליעדים שבועיים', at: 'ביעדים שבועיים',
  },
};

/**
 * Switching the restaurant between monthly and weekly budgets.
 *
 * Nothing is deleted and nothing is converted: budgets in the other series are
 * simply not looked up, and come back if you switch back. Converting would be
 * worse than not — a monthly ₪40,000 is not a weekly ₪9,238, nobody set that
 * number, and months are not 4.33 weeks for a restaurant that closes Mondays.
 *
 * @returns a dialog spec, or null when nothing is at stake.
 */
export function granularitySwitchDialog(from, to, counts = {}) {
  if (from === to) return null;
  const orphaned = counts[from] || 0;
  if (orphaned <= 0) return null;

  const a = NOUN[from], b = NOUN[to];

  // The count arrives in the SAME sentence as "נשמרים ולא נמחקים": leading with
  // it, as the previous copy did, framed the whole thing as a loss report.
  const kept = orphaned === 1
    ? `יעד ${a.one} אחד שכבר קבעת נשמר ולא נמחק, אך לא ייכלל בחישוב ${b.calc}.`
    : `${orphaned} היעדים ${a.budgets} שכבר קבעת נשמרים ולא נמחקים, אך לא ייכללו בחישוב ${b.calc}.`;

  return {
    title: `מעבר ${b.to}`,
    body: [
      `מעכשיו היעדים ייקבעו וייבדקו מול הרכש לפי ${b.plural}.`,
      // The no-conversion policy gets a clause of justification, because a
      // manager who is not told why will assume it is a bug.
      `${kept} סכומים לא מומרים בין ${a.unit} ל${b.unit}, כדי לא לייצר יעד שאיש לא קבע. מעבר חזרה ${a.to} יחזיר אותם לתוקף.`,
      // True at both call sites — the sheet and the settings page both only
      // stage this until save — and it pre-empts "I switched and nothing happened".
      'השינוי נכנס לתוקף בשמירה.',
    ],
    confirmLabel: `עבור ${b.to}`,
    cancelLabel: `הישאר ${a.at}`,
  };
}

/**
 * Deleting a classification.
 *
 * Its products are NOT deleted — they become unassigned, which lowers the
 * classification coverage this screen exists to raise. Naming that number is
 * what makes the consequence concrete; the old parenthetical "(לא יימחקו)" put
 * the one reassuring fact where the eye goes last.
 */
export function deleteClassificationDialog({ name, productCount = 0 }) {
  const moved = productCount === 1
    ? 'מוצר אחד יעבור ל"לא מסווג".'
    : `${productCount} מוצרים יעברו ל"לא מסווג".`;

  return {
    title: `מחיקת הסיווג "${name}"`,
    body: productCount > 0
      ? [
        // "אחוז הרכש המסווג" is the literal wording of the coverage figure on
        // the screen behind this dialog, so the user knows which number moves.
        `${moved} המוצרים עצמם לא נמחקים, וגם היסטוריית הרכש שלהם נשמרת — היא פשוט תפסיק להיזקף לסיווג הזה, ואחוז הרכש המסווג ירד בהתאם.`,
        'אפשר לסווג אותם לסיווג אחרת בכל שלב.',
      ]
      // Deleting an unused classification is routine tidy-up and should not get a
      // warning voice.
      : ['לא משויכים לסיווג הזה מוצרים, ולכן המחיקה לא משנה את סיווג הרכש ולא את הנתונים בניתוח.'],
    confirmLabel: 'מחק את הסיווג',
    cancelLabel: 'השאר את הסיווג',
    tone: 'danger',
  };
}
